import { ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY, AlgoliaIndex } from '@core/algolia';
import { ScriptCategory } from '@core/cookies';
import { chunkArray } from '@helpers/chunkArray';
import { useEffect, useRef, useState } from 'react';
import { InsightsClient } from 'search-insights';
import useAlgoliaUserToken from './useAlgoliaUserToken';
import useOneTrustCookieLevel from './useOneTrustCookieLevel';

const ALGOLIA_EVENT_NAMES = {
  PRODUCT_VIEWED: 'Product Viewed',
  PRODUCT_CLICKED: 'Product Clicked',
  PRODUCT_CONVERSION: 'Product Conversion',
  AUTOCOMPLETE_PRODUCT_CLICKED: 'Autocomplete Product Clicked',
  FILTER_VIEWED: 'Filter Viewed',
  FILTER_CLICKED: 'Filter Clicked',
};

interface UseAlgoliaEvents {
  aaLoaded: boolean;

  /**
   * A user viewed a product page from an email, newsletter, push notification, or any other page.
   */
  productViewed: (index: AlgoliaIndex, objectIDs: string[]) => void;

  /**
   * A user clicked on a search result. Send a click event to capture the search query, which item was clicked, and the position in the search results.
   */
  productClicked: (
    index: AlgoliaIndex,
    queryID: string | undefined | undefined,
    objectIDs: string[],
    positions: number[],
  ) => void;

  /**
   * A user clicked on a search result in the autocomplete menu.
   */
  autocompleteProductClicked: (
    index: AlgoliaIndex,
    queryID: string | undefined,
    objectIDs: string[],
    positions: number[],
  ) => void;

  /**
   * A user viewed a landing page.
   */
  filterViewed: (index: AlgoliaIndex, facets: Record<string, Record<string, number>> | undefined) => void;

  /**
   * A user selected a facet value.
   */
  filterClicked: (index: AlgoliaIndex, facet: string, value: string) => void;

  /**
   * A Product conversion event.
   */
  convertedObjectIDs: (index: AlgoliaIndex, objectIDs: string[]) => void;

  /**
   * A user converted on a search result.
   */
  convertedObjectIDsAfterSearch: (index: AlgoliaIndex, queryId: string, objectIDs: string[]) => void;

  /**
   * Set the Algolia query ID for the current session.
   */
  setAlgoliaQueryId: (queryId: string) => void;

  /**
   * Get the Algolia query ID for the current session.
   */
  getAlgoliaQueryId: () => string | undefined;

  /**
   * Clear the Algolia query ID for the current session.
   */
  clearAlgoliaQueryId: () => void;
}

export default function (): UseAlgoliaEvents {
  const aa = useRef<InsightsClient | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const queue = useRef<(() => void)[]>([]);
  const userToken = useAlgoliaUserToken();
  const cookieLevels = useOneTrustCookieLevel();

  useEffect(() => {
    async function loadAA(): Promise<void> {
      if (cookieLevels.indexOf(ScriptCategory.Performance) === -1) {
        return;
      }

      const searchInsightsModule = await import('search-insights');

      aa.current = searchInsightsModule.default;

      aa.current('init', { appId: ALGOLIA_APP_ID, apiKey: ALGOLIA_SEARCH_API_KEY });
      aa.current('setUserToken', userToken);

      setLoaded(true);
    }

    loadAA();
  }, []);

  useEffect(() => {
    if (loaded && queue.current.length > 0) {
      console.log('Processing queue');

      console.log(queue);

      for (const event of queue.current) {
        event();
      }

      queue.current = [];
    }
  }, [loaded]);

  const productViewed = (index: AlgoliaIndex, objectIDs: string[]): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => productViewed(index, objectIDs));
      return;
    }

    if (objectIDs.length === 0) {
      console.warn('No objectIDs provided for productViewed event');
      return;
    }

    const chunkedObjectIDs = chunkArray(objectIDs, 20);

    for (const chunkedObjectIDsSet of chunkedObjectIDs) {
      aa.current('viewedObjectIDs', {
        index,
        objectIDs: chunkedObjectIDsSet,
        eventName: ALGOLIA_EVENT_NAMES.PRODUCT_VIEWED,
        userToken,
      });
    }
  };

  const productClicked = (
    index: AlgoliaIndex,
    queryID: string | undefined,
    objectIDs: string[],
    positions: number[],
  ): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => productClicked(index, queryID, objectIDs, positions));
      return;
    }

    if (!queryID) {
      console.warn('No queryID provided for productClicked event');
      return;
    }
    if (objectIDs.length === 0 || positions.length === 0) {
      console.warn('No objectIDs or positions provided for productClicked event');
      return;
    }

    aa.current('clickedObjectIDsAfterSearch', {
      index,
      queryID,
      positions,
      objectIDs,
      eventName: ALGOLIA_EVENT_NAMES.PRODUCT_CLICKED,
      userToken,
    });
  };

  // To be used in the future if we display products in the autocomplete
  const autocompleteProductClicked = (
    index: AlgoliaIndex,
    queryID: string | undefined,
    objectIDs: string[],
    positions: number[],
  ): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => autocompleteProductClicked(index, queryID, objectIDs, positions));
      return;
    }

    if (!queryID) {
      console.warn('No queryID provided for autocompleteProductClicked event');
      return;
    }

    if (objectIDs.length === 0 || positions.length === 0) {
      console.warn('No objectIDs or positions provided for autocompleteProductClicked event');
      return;
    }

    aa.current('clickedObjectIDsAfterSearch', {
      index,
      queryID,
      positions,
      objectIDs,
      eventName: ALGOLIA_EVENT_NAMES.AUTOCOMPLETE_PRODUCT_CLICKED,
      userToken,
    });
  };

  const filterViewed = (index: AlgoliaIndex, facets: Record<string, Record<string, number>> | undefined): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => filterViewed(index, facets));
      return;
    }

    if (!facets) {
      console.warn('No facets provided for filterViewed event');
      return;
    }

    const facetKeys = Object.keys(facets);

    if (facetKeys.length === 0) {
      console.warn('No facets provided for filterViewed event');
      return;
    }

    for (const facetKey of facetKeys) {
      const facet = facets[facetKey];
      const facetValues = Object.keys(facet);
      // Algolia only supports 10 filters at a time so we need to chunk them into arrays of 10
      const chunkedFacetValues = chunkArray(facetValues, 10);

      for (const facetValueSet of chunkedFacetValues) {
        const encodedFilters: string[] = [];

        for (const facetValue of facetValueSet) {
          encodedFilters.push(`${encodeURIComponent(facetKey)}:${encodeURIComponent(facetValue)}`);
        }

        aa.current('viewedFilters', {
          index,
          filters: encodedFilters,
          eventName: ALGOLIA_EVENT_NAMES.FILTER_VIEWED,
          userToken,
        });
      }
    }
  };

  const filterClicked = (index: AlgoliaIndex, facet: string, value: string): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => filterClicked(index, facet, value));
      return;
    }

    if (facet.length === 0) {
      console.warn('No facet provided for filterClicked event');
      return;
    }

    if (value.length === 0) {
      console.warn('No value provided for filterClicked event');
      return;
    }

    aa.current('clickedFilters', {
      index,
      filters: [`${encodeURIComponent(facet)}:${encodeURIComponent(value)}`],
      eventName: ALGOLIA_EVENT_NAMES.FILTER_CLICKED,
      userToken,
    });
  };

  const convertedObjectIDs = (index: AlgoliaIndex, objectIDs: string[]): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => convertedObjectIDs(index, objectIDs));
      return;
    }

    if (objectIDs.length === 0) {
      console.warn('No facet provided for filterClicked event');
      return;
    }

    aa.current('convertedObjectIDs', {
      index,
      objectIDs,
      eventName: ALGOLIA_EVENT_NAMES.PRODUCT_CONVERSION,
      userToken,
    });
  };

  const convertedObjectIDsAfterSearch = (index: AlgoliaIndex, queryId: string, objectIDs: string[]): void => {
    if (!aa.current) {
      console.warn('No aa client found, adding to queue');

      queue.current.push(() => convertedObjectIDsAfterSearch(index, queryId, objectIDs));
      return;
    }

    if (objectIDs.length === 0) {
      console.warn('No object IDs provided for convertedObjectIDsAfterSearch event');
      return;
    }

    aa.current('convertedObjectIDsAfterSearch', {
      index,
      queryID: queryId,
      objectIDs,
      eventName: ALGOLIA_EVENT_NAMES.PRODUCT_CONVERSION,
      userToken,
    });
  };

  const setAlgoliaQueryId = (queryId: string): void => {
    try {
      localStorage.setItem('AlgoliaQueryID', queryId);
    } catch {
      console.error('Failed to set Algolia query ID');
    }
  };

  const getAlgoliaQueryId = (): string | undefined => {
    try {
      return localStorage.getItem('AlgoliaQueryID') as string | undefined;
    } catch {
      console.error('Failed to set Algolia query ID');
      return undefined;
    }
  };

  const clearAlgoliaQueryId = (): void => {
    try {
      localStorage.removeItem('AlgoliaQueryID');
    } catch {
      console.error('Failed to set Algolia query ID');
    }
  };

  return {
    aaLoaded: loaded,
    productViewed,
    productClicked,
    autocompleteProductClicked,
    filterViewed,
    filterClicked,
    convertedObjectIDs,
    convertedObjectIDsAfterSearch,
    setAlgoliaQueryId,
    getAlgoliaQueryId,
    clearAlgoliaQueryId,
  };
}
